import {
  Checkbox,
  DefaultButton,
  Dialog,
  DialogFooter,
  DialogType,
  PrimaryButton,
  TextField,
} from "@fluentui/react";
import { Field, Form, Formik } from "formik";
import React, { useContext, useState } from "react";
import { ColumnOptionsContext } from "./ColumnOptions";

type Props = {
  close: () => void;
  userAppSettingId: number | null;
};

export const ViewDialog = ({ close, userAppSettingId }: Props) => {
  const {
    views,
    selectedView,
    onCreateView,
    onUpdateView,
    isViewLoading,
    strings,
    id,
  } = useContext(ColumnOptionsContext);
  const [didTrySubmit, setDidTrySubmit] = useState(false);

  const isEdit = userAppSettingId !== null;
  const isLoading = isViewLoading;

  const onSubmit = (data: any) => {
    // trim data before storing
    data.userAppSettingName = data.userAppSettingName.trim();
    data.userAppSettingDescription = data.userAppSettingDescription.trim();
    if (!isEdit) {
      onCreateView(data);
    }
    //
    else {
      onUpdateView(data);
    }
  };

  return (
    <Dialog
      hidden={false}
      onDismiss={close}
      dialogContentProps={{
        type: DialogType.normal,
        title: isEdit ? strings.editViewDialogLabel : strings.createViewDialogLabel,
      }}
      modalProps={{ isBlocking: true }}
    >
      <Formik
        initialValues={
          isEdit
            ? (selectedView as any)
            : {
                userAppSettingName: "",
                userAppSettingDescription: "",
                isDefault: false,
              }
        }
        onSubmit={onSubmit}
        validateOnChange={didTrySubmit}
        validateOnBlur={didTrySubmit}
      >
        {({ submitForm }) => {
          return (
            <Form>
              <Field
                name="userAppSettingName"
                validate={(value: any) => {
                  if (!value || value.trim() === "")
                    return strings.requiredErrorMessage;
                  let doesExist = !!views.find(
                    (s) => s.userAppSettingName === value.trim()
                  );
                  if (isEdit) {
                    doesExist = !!views
                      .filter(
                        (s) =>
                          s.userAppSettingId !== selectedView?.userAppSettingId
                      )
                      .find((s) => s.userAppSettingName === value);
                  }
                  if (doesExist) {
                    return strings.viewExistsErrorMessage;
                  }
                  return undefined;
                }}
              >
                {({ field, form, meta }: any) => (
                  <TextField
                    autoFocus
                    required
                    label={strings.viewDialogNameFieldLabel}
                    maxLength={128}
                    value={field.value}
                    onChange={(_, v) =>
                      form.setFieldValue("userAppSettingName", v)
                    }
                    onBlur={field.onBlur}
                    name={field.name}
                    errorMessage={meta.error}
                    id={id ? `${id}-field-name` : undefined}
                  />
                )}
              </Field>
              <Field name="userAppSettingDescription">
                {({ field, form, meta }: any) => (
                  <TextField
                    label={strings.viewDialogDescriptionFieldLabel}
                    maxLength={128}
                    value={field.value}
                    onChange={(_, v) =>
                      form.setFieldValue("userAppSettingDescription", v)
                    }
                    onBlur={field.onBlur}
                    name={field.name}
                    errorMessage={meta.error}
                    id={id ? `${id}-field-desc` : undefined}
                    styles={{ root: { marginTop: 12 } }}
                  />
                )}
              </Field>
              <Field name="isDefault" type="checkbox">
                {({ field, form }: any) => (
                  <Checkbox
                    label={strings.viewDialogFavoriteFieldLabel}
                    checked={!!field.value}
                    onChange={(_, checked) =>
                      form.setFieldValue("isDefault", checked)
                    }
                    id={id ? `${id}-checkbox-star` : undefined}
                    styles={{ root: { marginTop: 16 } }}
                  />
                )}
              </Field>
              <DialogFooter>
                <DefaultButton
                  onClick={close}
                  disabled={isLoading}
                  text="Cancel"
                />
                <PrimaryButton
                  onClick={() => {
                    setDidTrySubmit(true);
                    submitForm();
                  }}
                  disabled={isLoading}
                  id={id ? `${id}-btn-save` : undefined}
                  text={strings.viewDialogSaveButtonLabel}
                />
              </DialogFooter>
            </Form>
          );
        }}
      </Formik>
    </Dialog>
  );
};
